import React from "react";
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  Grid,
  TextField,
  Button,
  Tooltip,
  IconButton,
  Divider,
  Checkbox,
  Chip,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import Label from "../../../../components/Label/Label";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import "./ProcessCapabilityConfigs.scss";
import {
  distributionCurvesOptions,
  limitTypeOptions,
  specLimitTypeOptions,
} from "../constants";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

const ProcessCapabilityConfigs = ({
  unitOfOperationId,
  updateState,
  handleGenerate,
  configState,
  onClickClearCurrentConfig,
  configSetIds,
  disableAllFields,
  setShowConfigPanel,
  setSelectedTab,
}) => {
  const onChangeSpecLimit = (e) => {
    const value = e.target.value;
    updateState("specLimitType", value);
    if (value !== "User Defined") {
      updateState("lowerSpecLimit", "");
      updateState("upperSpecLimit", "");
      updateState("specificationTarget", "");
    }
    if (value !== "User Preset") {
      updateState("specLimitSetId", "");
    }
  };

  const onChangeControlLimit = (e) => {
    const value = e.target.value;
    updateState("controlLimitType", value);
    if (value !== "User Defined") {
      updateState("lowerControlLimit", "");
      updateState("upperControlLimit", "");
    }
    if (value !== "User Preset") {
      updateState("controlLimitSetId", "");
    }
  };

  const isApplyDisabled = () => {
    const isSpecPresetInvalid =
      configState.specLimitType === "User Preset" &&
      !configState.specLimitSetId;

    const isSpecDefinedInvalid =
      configState.specLimitType === "User Defined" &&
      !(
        (configState.lowerSpecLimit && configState.upperSpecLimit)
        // ||
        // configState.specificationTarget
      );

    const isControlPresetInvalid =
      configState.controlLimitType === "User Preset" &&
      !configState.controlLimitSetId;

    const isControlDefinedInvalid =
      configState.controlLimitType === "User Defined" &&
      !(configState.lowerControlLimit && configState.upperControlLimit);

    return (
      !unitOfOperationId ||
      isSpecPresetInvalid ||
      isSpecDefinedInvalid ||
      isControlPresetInvalid ||
      isControlDefinedInvalid
    );
  };

  const handleCurveChange = (selected) => {
    updateState("selectedCurves", selected);
  };

  return (
    <Box className="plot-config-wrapper">
      <Box className="scrollable-config">
        {/* Specification Limit */}
        <Card elevation={0} className="filter-card plot-config-card">
          <CardContent className="toggle-section">
            <Typography variant="caption" sx={{ pb: 0.5, color: "#c62828" }}>
              Please generate the graph before configuring Limits.
            </Typography>
            
            <Label text="Specification Limit" fontWeight="500" />
            <FormControl
              fullWidth
              size="small"
              className="dropdown-field"
              sx={{ mt: 1 }}
              disabled={disableAllFields}
              error={
                configState.specLimitType === "User Preset" &&
                !configState.specLimitSetId
              }
            >
              <InputLabel>Select Limit</InputLabel>
              <Select
                value={configState.specLimitType}
                onChange={
                  (e) => onChangeSpecLimit(e)
                  //  updateState("specLimitType", e.target.value)
                }
                label="Select Limit"
              >
                {/* <MenuItem value="">
                  <em>None</em>
                </MenuItem> */}
                {specLimitTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {configState.specLimitType === "User Preset" && (
              <FormControl
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                disabled={disableAllFields}
                error={!configState.specLimitSetId}
              >
                <InputLabel id="spec-site-label">Select Set</InputLabel>
                <Select
                  labelId="spec-site-label"
                  label="Select Set"
                  value={configState.specLimitSetId || ""}
                  onChange={(e) =>
                    updateState("specLimitSetId", e.target.value)
                  }
                >
                  {Array.isArray(configSetIds?.specification_limit_options) &&
                  configSetIds?.specification_limit_options.length > 0 ? (
                    configSetIds.specification_limit_options.map((site) => (
                      <MenuItem
                        key={site?.spec_limit_id}
                        value={site?.spec_limit_id}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <Label text={site?.identifier} />
                          <Tooltip
                            arrow
                            placement="right"
                            title={
                              <Box
                                sx={{
                                  borderRadius: 2,
                                  boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                                  p: 2,
                                  backgroundColor: "#fff",
                                  minWidth: 220,
                                }}
                              >
                                <Label
                                  text="Specification Limits"
                                  fontWeight="600"
                                  fontSize="14px"
                                  color="#1976d2"
                                />
                                <Divider sx={{ my: 1 }} />
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "80px 1fr",
                                    rowGap: 0.5,
                                  }}
                                >
                                  <Label text="LSL:" fontWeight="500" />
                                  <Label text={site.lower_spec ?? "N/A"} />
                                  <Label text="USL:" fontWeight="500" />
                                  <Label text={site.upper_spec ?? "N/A"} />
                                  <Label text="Target:" fontWeight="500" />
                                  <Label text={site.process_target ?? "N/A"} />
                                </Box>
                              </Box>
                            }
                          >
                            <IconButton
                              size="small"
                              sx={{
                                p: 0.5,
                                color: "#1976d2",
                                "&:hover": {
                                  color: "#0d47a1",
                                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                                },
                              }}
                            >
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <Label text="No sets available" />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            )}

            {configState.specLimitType === "User Defined" && (
              <Grid container spacing={2} pt={1}>
                <Grid item xs={6}>
                  <TextField
                    type="number"
                    label="Lower Spec Limit"
                    size="small"
                    fullWidth
                    value={configState.lowerSpecLimit}
                    onChange={(e) =>
                      updateState("lowerSpecLimit", e.target.value)
                    }
                    disabled={disableAllFields}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    type="number"
                    label="Upper Spec Limit"
                    size="small"
                    fullWidth
                    value={configState.upperSpecLimit}
                    onChange={(e) =>
                      updateState("upperSpecLimit", e.target.value)
                    }
                    disabled={disableAllFields}
                    required
                  />
                </Grid>
                <Grid item xs={12} className="padding-top-8">
                  <TextField
                    type="number"
                    label="Target"
                    size="small"
                    fullWidth
                    value={configState.specificationTarget}
                    onChange={(e) =>
                      updateState("specificationTarget", e.target.value)
                    }
                    disabled={disableAllFields}
                  />
                </Grid>

                {!(
                  (configState.lowerSpecLimit && configState.upperSpecLimit)
                  // ||
                  // configState.specificationTarget
                ) && (
                  <Grid item xs={12} sx={{ paddingTop: "8px !important" }}>
                    <Box sx={{ color: "error.main", ml: 1 }}>
                      <Label
                        fontSize="12px"
                        color="inherit"
                        text="Enter the lower and upper spec limit values."
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Control Limit */}
            <Box sx={{ mt: 1 }}>
              <Label text="Control Limit" fontWeight="500" />
              <FormControl
                fullWidth
                size="small"
                className="dropdown-field"
                disabled={disableAllFields}
                error={
                  configState.controlLimitType === "User Preset" &&
                  !configState.controlLimitSetId
                }
              >
                <InputLabel>Select Limit</InputLabel>
                <Select
                  value={configState.controlLimitType}
                  onChange={
                    (e) => onChangeControlLimit(e)
                    // updateState("controlLimitType", e.target.value)
                  }
                  label="Select Limit"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {limitTypeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {configState.controlLimitType === "User Preset" && (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  disabled={disableAllFields}
                  error={!configState.controlLimitSetId}
                >
                  <InputLabel id="control-site-label">Select Set</InputLabel>
                  <Select
                    labelId="control-site-label"
                    value={configState.controlLimitSetId || ""}
                    onChange={(e) =>
                      updateState("controlLimitSetId", e.target.value)
                    }
                    label="Select Set"
                  >
                    {Array.isArray(configSetIds?.control_limit_options) &&
                    configSetIds?.control_limit_options.length > 0 ? (
                      configSetIds.control_limit_options.map((site) => (
                        <MenuItem
                          key={site?.stat_limit_id}
                          value={site?.stat_limit_id}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <Label text={site?.set_id} />
                            <Tooltip
                              arrow
                              placement="right"
                              title={
                                <Box
                                  sx={{
                                    borderRadius: 2,
                                    boxShadow:
                                      "0 4px 10px rgba(182, 180, 180, 0.15)",
                                    p: 2,
                                    backgroundColor: "#fff",
                                    minWidth: 220,
                                  }}
                                >
                                  <Label
                                    text="Control Limits"
                                    fontWeight="600"
                                    fontSize="14px"
                                    color="#1976d2"
                                  />
                                  <Divider sx={{ my: 1 }} />
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: "100px 1fr",
                                      rowGap: 0.5,
                                    }}
                                  >
                                    <Label text="UCL:" fontWeight="500" />
                                    <Label text={site.upper_control ?? "N/A"} />
                                    <Label text="LCL:" fontWeight="500" />
                                    <Label text={site.lower_control ?? "N/A"} />
                                  </Box>
                                </Box>
                              }
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  p: 0.5,
                                  color: "#1976d2",
                                  "&:hover": {
                                    color: "#0d47a1",
                                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                                  },
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <Label text="No sets available" />
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}

              {configState.controlLimitType === "User Defined" && (
                <Grid container spacing={2} pt={1}>
                  <Grid item xs={6}>
                    <TextField
                      type="number"
                      label="Lower Limit"
                      size="small"
                      fullWidth
                      value={configState.lowerControlLimit}
                      onChange={(e) =>
                        updateState("lowerControlLimit", e.target.value)
                      }
                      disabled={disableAllFields}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      type="number"
                      label="Upper Limit"
                      size="small"
                      fullWidth
                      value={configState.upperControlLimit}
                      onChange={(e) =>
                        updateState("upperControlLimit", e.target.value)
                      }
                      disabled={disableAllFields}
                    />
                  </Grid>

                  {!(
                    configState.lowerControlLimit &&
                    configState.upperControlLimit
                  ) && (
                    <Grid item xs={12} sx={{ paddingTop: "8px !important" }}>
                      <Box sx={{ color: "error.main", ml: 1 }}>
                        <Label
                          fontSize="12px"
                          color="inherit"
                          text="Enter the lower and upper control limit values."
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              )}
            </Box>

            {/* <Box sx={{ mt: 1 }}>
              <Label text="Target Limit" fontWeight="500" />
              <FormControl
                fullWidth
                size="small"
                className="dropdown-field"
                sx={{ mt: 1 }}
              >
                <InputLabel>Select Limit</InputLabel>
                <Select
                  value={configState.targetLimitType}
                  onChange={(e) => onChangeTargetLimit(e)}
                  label="Select Limit"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {targetLimitTypeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {configState.targetLimitType === "User Preset" && (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel id="spec-site-label">Select Set</InputLabel>
                  <Select
                    labelId="spec-site-label"
                    label="Select Set"
                    value={configState.targetLimitSetId || ""}
                    onChange={(e) =>
                      updateState("targetLimitSetId", e.target.value)
                    }
                  >
                    {(Array.isArray(configSetIds?.specification_limit_options)
                      ? configSetIds?.specification_limit_options
                      : []
                    )?.map((site) => (
                      <MenuItem
                        key={site?.spec_limit_id}
                        value={site?.spec_limit_id}
                      >
                        {site?.identifier}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {configState.targetLimitType === "User Defined" && (
                <Box sx={{ pt: 1 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Target Limit"
                    size="small"
                    value={configState.targetLimit}
                    onChange={(e) => updateState("targetLimit", e.target.value)}
                  />
                </Box>
              )}
            </Box> */}
          </CardContent>
        </Card>

        {/* Scale */}
        {/* <Card elevation={0} className=" filter-card plot-config-card">
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={configState.showZoomScale}
                  onChange={
                    (e) => onChangeZoomScale(e)
                    // updateState("showZoomScale", e.target.checked)
                  }
                />
              }
              label="Scale"
            />

            {configState.showZoomScale && (
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                  type="number"
                    label="X Min"
                    size="small"
                    fullWidth
                    value={configState.zoomXMin || ""}
                    onChange={(e) => updateState("zoomXMin", e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                  type="number"
                    label="X Max"
                    size="small"
                    fullWidth
                    value={configState.zoomXMax || ""}
                    onChange={(e) => updateState("zoomXMax", e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Y Min"
                    size="small"
                    fullWidth
                    value={configState.zoomYMin || ""}
                    onChange={(e) => updateState("zoomYMin", e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                  type="number"
                    label="Y Max"
                    size="small"
                    fullWidth
                    value={configState.zoomYMax || ""}
                    onChange={(e) => updateState("zoomYMax", e.target.value)}
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card> */}

        {/* Bin Width Slider */}
        {/* <Card elevation={0} className="filter-card  plot-config-card">
          <CardContent>
            <Label text="Bin Width" fontWeight="500" />
            <Box px={1}>
              <Slider
                value={configState.binWidth || 1}
                onChange={(e, val) => updateState("binWidth", val)}
                min={1}
                max={5}
                step={1}
                marks
                valueLabelDisplay="auto"
              />
            </Box>
          </CardContent>
        </Card> */}

        <Card elevation={0} className="filter-card plot-config-card">
          <CardContent className="toggle-section">
            <Box>
              <Label text="Add Distribution Curves" fontWeight="500" />

              <FormControl
                fullWidth
                size="small"
                className="dropdown-field"
                sx={{ mt: 1 }}
                disabled={disableAllFields}
              >
                <InputLabel>Select Curves</InputLabel>
                <Select
                  multiple
                  value={
                    Array.isArray(configState.selectedCurves)
                      ? configState.selectedCurves
                      : typeof configState.selectedCurves === "string"
                        ? configState.selectedCurves.split(",")
                        : []
                  }
                  onChange={(e) => {
                    const selected = e.target.value;

                    // Ensure "norm" stays in the selected list
                    if (!selected.includes("norm")) {
                      selected.push("norm");
                    }

                    handleCurveChange(selected);
                  }}
                  label="Select Curves"
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const label = distributionCurvesOptions?.find(
                          (option) => option.value === value,
                        )?.label;

                        const isLocked = value === "norm";

                        return (
                          <Chip
                            key={value}
                            label={label || value}
                            size="small"
                            // onDelete={
                            //   !isLocked
                            //     ? (event) => {
                            //         event.preventDefault(); // ✅ stops default behavior
                            //         event.stopPropagation(); // ✅ stops propagation to Select
                            //         const updated = selected.filter(
                            //           (val) => val !== value
                            //         );
                            //         updateState("selectedCurves", updated);
                            //       }
                            //     : undefined
                            // }
                            style={
                              isLocked
                                ? {
                                    backgroundColor: "#e0e0e0",
                                    fontWeight: 500,
                                  }
                                : {}
                            }
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {distributionCurvesOptions
                    ?.filter((opt) => opt.value !== "Johnson")
                    .map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box display="flex" alignItems="center">
                          <Checkbox
                            checked={configState.selectedCurves?.includes(
                              option.value,
                            )}
                            disableRipple
                            style={{ padding: 0, marginRight: 8 }}
                            disabled={option.value === "norm"}
                          />
                          {option.label}
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
            {/* {configState.selectedCurves?.includes("johnsonsb") && (
              <Box sx={{ mt: 1 }}>
                <Label text="Johnson Sb" fontWeight="500" />
                <Grid container spacing={2} pt={1}>
                  <Grid item xs={6}>
                    <TextField
                      type="number"
                      label="Lower Bound"
                      size="small"
                      fullWidth
                      value={configState.johnson_sb_lower_bound}
                      onChange={(e) =>
                        updateState("johnson_sb_lower_bound", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      type="number"
                      label="Upper Bound"
                      size="small"
                      fullWidth
                      value={configState.johnson_sb_upper_bound}
                      onChange={(e) =>
                        updateState("johnson_sb_upper_bound", e.target.value)
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            )} */}
          </CardContent>
        </Card>

        <Card elevation={0} className="filter-card plot-config-card">
          <CardContent className="toggle-section">
            <FormControlLabel
              sx={{ pl: "8px", pr: "8px" }}
              control={
                <Switch
                  checked={configState.show_target}
                  onChange={(e) => updateState("show_target", e.target.checked)}
                />
              }
              label={<Label text="Show Target" fontWeight="500" />}
              disabled={disableAllFields}
            />
            <FormControlLabel
              sx={{ pl: "8px", pr: "8px" }}
              control={
                <Switch
                  checked={configState.show_grid}
                  onChange={(e) => updateState("show_grid", e.target.checked)}
                />
              }
              label={<Label text="Show Grid" fontWeight="500" />}
              disabled={disableAllFields}
            />
          </CardContent>
        </Card>
      </Box>

      {/* Footer */}
      <Box className="plot-footer-sticky">
        <Card elevation={0} className="filter-card plot-footer-btn-card">
          <CardContent>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Tooltip title="Previous">
                <IconButton
                  onClick={() => setSelectedTab("batchFilters")}
                  size="small"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                    borderRadius: "8px",
                    px: 1,
                    py: 0.6,
                  }}
                >
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                className="close-btn"
                onClick={() => setShowConfigPanel(false)}
              >
                Close
              </Button>
              <Button
                variant="outlined"
                onClick={onClickClearCurrentConfig}
                disabled={disableAllFields}
              >
                Clear Config
              </Button>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={isApplyDisabled() || disableAllFields}
              >
                Apply
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ProcessCapabilityConfigs;
